import MenuIcon from "@/assets/images/icons/menu-icon.svg";
import NavIconBookmark from "@/assets/images/icons/nav-icon-bookmark.svg";
import NavIconHang from "@/assets/images/icons/nav-icon-hang.svg";
import OpenStudioLogo from "@/assets/images/openstudio-logo-main.svg";
import OpenStudioIcon from "@/assets/images/openstudio-main.svg";
import { AccountMenu } from "@/components/account/AccountMenu";
import { AnnouncementsDropdown } from "@/components/AnnouncementsDropdown";
import { BookmarkManager } from "@/components/BookmarkManager";
import { ExploreMenuDropdown } from "@/components/ExploreMenuDropdown";
import ExternalLink from "@/components/ExternalLink";
import { LiveSessionsDropdown } from "@/components/LiveSessionsDropdown";
import { SubscriptionHeaderAlerts } from "@/components/subscription/SubscriptionHeaderAlerts";
import { useCourse } from "@/hooks/useCourse";
import { useAuth } from "@/lib/AuthContext";
import { URLS } from "@/lib/constants";
import { useDrawer } from "@/lib/DrawerContext";
import { isMemberTier } from "@/lib/membershipTier";
import { resetToRoot } from "@/lib/navigation";
// import { resolveUserHomeUrl } from "@/lib/userHomeUrl";
import { CardTitle } from "@/components/ui/Typography";
import { useIsMobile } from "@/lib/useDeviceType";
import { Ionicons } from "@expo/vector-icons";
import { type Href, usePathname } from "expo-router";
import React, { useState } from "react";
import { Platform, Pressable, StatusBar, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useMedia, useTheme, XStack, YStack } from "tamagui";
import { GlobalSearchManager } from "./search/GlobalSearchManager";

const HEADER_NAV_ICON_SLOT = { w: 36, h: 36 } as const;
/** Bookmark + announcements bell — same visual size in the header row */
const HEADER_ACTION_ICON = 36 as const;

/** `/courses/<slug>[/...]` → slug; null on the hub, skill paths, or elsewhere. */
function courseSlugFromPathname(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  const m = /^\/courses\/([^/?#]+)/.exec(pathname);
  const slug = m?.[1] ?? null;
  if (!slug || slug === "skillpath") return null;
  return slug;
}

export type NavItem = { label: string; href: Href };

type Props = {
  title?: string;
  navItems?: NavItem[];
  onBack?: () => void;
  showBack?: boolean;
  right?: React.ReactNode;
  left?: React.ReactNode;
  onSearch?: (q: string) => void;
  sticky?: boolean; // makes it stick on web/tablet
  elevate?: boolean; // shadow/border accent
  showBookmark?: boolean;
};

export default function ResponsiveHeader({
  title,
  navItems = [],
  onBack,
  showBack,
  // right,
  left,
  onSearch,
  sticky,
  elevate,
  // showBookmark = false,
}: Props) {
  // const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null)
  // const pathname = usePathname()
  // const [q, setQ] = useState('')
  const isWeb = Platform.OS === "web";
  const insets = useSafeAreaInsets();
  const { openDrawer } = useDrawer();
  const { user, membershipTier, membershipLoading } = useAuth();
  const isMobile = useIsMobile();

  // Header logo → /courses hub (dashboard/setup via Personal Hub "For you").
  const logoUrl = URLS.APP_HOME;
  // Previous: logo → personal dashboard (in-app) or WordPress.
  // const logoUrl = useMemo(
  //   () =>
  //     resolveUserHomeUrl(
  //       preferenceMetadata ?? undefined,
  //       membershipTier,
  //       membershipLoading,
  //     ),
  //   [preferenceMetadata, membershipTier, membershipLoading],
  // );
  // Handle logout with Mixpanel reset (commented out)
  // const { signOut } = useAuth()
  // const handleLogout = async () => {
  //   try {
  //     const { error } = await signOut()
  //     if (error) {
  //       __DEV__ && logger.debug('ℹLogout completed (session was already cleared)')
  //     } else {
  //       __DEV__ && logger.debug('User logged out successfully')
  //     }
  //   } catch (error) {
  //     logger.error('Failed to logout:', error)
  //   }
  // }

  // const submit = () => {
  //   const qq = q.trim()
  //   if (!qq) return
  //   onSearch?.(qq)
  //   setSearchOpen(false)
  // }

  const media = useMedia();
  const theme = useTheme();
  // const iconSize = media.gtMd ? 36 : media.gtSm ? 32 : 28

  /** Default content width; lesson detail uses full viewport width. */
  const CONTAINER_MAX = 1140;
  const pathname = usePathname();
  const isLessonDetailRoute =
    isWeb &&
    Boolean(pathname && /^\/courses\/[^/]+\/[^/]+$/.test(pathname));
  // Orange used for Live beacon and header item hover (same as LiveSessionsDropdown)
  const HEADER_HOVER_ORANGE = "rgba(242, 102, 58, 1)";

  const [hoverHang, setHoverHang] = useState(false);
  const [hoverBookmark, setHoverBookmark] = useState(false);

  // Course the user is inside (course page, lesson, collections) — the
  // course page already fetched it, so this is normally a cache hit.
  const headerCourseSlug = courseSlugFromPathname(pathname);
  const { data: headerCourse } = useCourse(headerCourseSlug ?? "");
  // Per-course opt-out (CMS "Hide Live & The Hang in header"), desktop web only.
  const hideCommunityNav =
    isWeb &&
    !isMobile &&
    Boolean(headerCourseSlug) &&
    headerCourse?.metadata?.hide_community_nav === true;

  // Default navigation items. Native: non-subscribed users don't see Live or
  // The Hang at all (web keeps today's behavior; shown while the tier loads
  // to avoid a layout jump for members).
  const defaultNavItems = [
    { label: "Explore", url: URLS.EXPLORE },
    { label: "Live", url: URLS.EVENTS },
    { label: "The Hang", url: URLS.COMMUNITY },
  ].filter(
    (item) =>
      (item.label !== "Live" && item.label !== "The Hang") ||
      (isWeb && !hideCommunityNav) ||
      (!isWeb && (membershipLoading || isMemberTier(membershipTier))),
  );

  // const items = useMemo(() => navItems.map(i => ({
  //   ...i,
  //   hrefStr: String(i.href),
  // })), [navItems])

  const logoGlyph = media.gtSm ? (
    <OpenStudioLogo width={media.gtMd ? 161 : 134} height={media.gtMd ? 34 : 28} />
  ) : (
    <OpenStudioIcon width={28} height={28} />
  );

  // Native logo tap: popToTop the stack back to the /courses root. Pops when
  // we're deeper in, and is a no-op (no remount) when we're already there.
  const handleNativeLogoPress = () => {
    resetToRoot(logoUrl as Href);
  };

  const nativeDrillInTitle = Boolean(title && !isWeb && showBack);
  const phoneDrillInTitle = nativeDrillInTitle && isMobile;
  const tabletDrillInTitle = nativeDrillInTitle && !isMobile;

  const headerContent = (
    <YStack
      bg="$header"
      width="100%"
      // sticky header (web/tablet)
      position={sticky ? "sticky" : "relative"}
      // No padding - align with section content area
      top={sticky ? 0 : "auto"}
      zi={sticky ? 100 : 1}
      bbw={elevate ? 1 : 0}
      bc="$header"
    >
      {/* Centered container with max-width 1140px + padding like sections */}
      <XStack
        ai="center"
        jc="space-between"
        w="100%"
        position="relative"
        {...(isLessonDetailRoute ? {} : { maw: CONTAINER_MAX, mx: 'auto' })}
        px={16}
        h={media.gtMd ? 85 : media.gtSm ? 72 : 64}
      >
        {/* Left: brand / back */}
        <XStack ai="center" gap="$2" f={1} fs={1} minWidth={0}>
          {left}

          {/* Back button (mobile only, when needed) */}
          {showBack && !isWeb && (
            <Pressable onPress={onBack} hitSlop={8} style={{ marginRight: 6 }}>
              <Ionicons name="chevron-back" size={22} color="white" />
            </Pressable>
          )}

          {/* Logo - always show when no back button */}
          {(!showBack || isWeb) &&
            (isWeb ? (
              <ExternalLink url={logoUrl} replace>
                {logoGlyph}
              </ExternalLink>
            ) : (
              <Pressable onPress={handleNativeLogoPress} hitSlop={8}>
                {logoGlyph}
              </Pressable>
            ))}

          {tabletDrillInTitle ? (
            <CardTitle
              color="white"
              fontWeight="600"
              numberOfLines={1}
              minWidth={0}
              flexShrink={1}
            >
              {title}
            </CardTitle>
          ) : null}
        </XStack>

        {phoneDrillInTitle ? (
          <XStack
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            ai="center"
            jc="center"
            pointerEvents="none"
          >
            <CardTitle
              color="white"
              fontWeight="600"
              numberOfLines={1}
              maw="58%"
              minWidth={0}
              flexShrink={1}
              ta="center"
            >
              {title}
            </CardTitle>
          </XStack>
        ) : null}

        {/* Center spacer — skip on drill-in: phone uses a centered overlay, tablet
            lets the left-aligned title grow up to the nav items before truncating. */}
        {!nativeDrillInTitle ? (
        <XStack f={1} gap="$4" display="none" $gtSm={{ display: "flex" }}>
          {/* Empty space */}
        </XStack>
        ) : null}
        {/* Custom nav items */}
        {/* {items.map((item) => {
            const active = pathname === item.hrefStr || pathname.startsWith(item.hrefStr + '/')
            return (
              <Link key={item.label} href={item.href} asChild>
                <Pressable>
                  <Text col={active ? ACTIVE : '$muted'} fow="700">
                    {item.label}
                  </Text>
                </Pressable>
              </Link>
            )
          })} */}

        {/* Right: navigation items + account */}
        <XStack ai="center" gap={isMobile ? 4 : 16} ml="$3" flexShrink={0}>
          {/* Navigation items - desktop/tablet only */}
          {!isMobile && (
            <>
              {defaultNavItems.map((item) =>
                item.label === "Live" ? (
                  <View
                    key={item.label}
                    style={{
                      flexShrink: 0,
                    }}
                  >
                    <LiveSessionsDropdown
                      membershipTier={membershipTier}
                      membershipLoading={membershipLoading}
                      showLabel
                      iconSize={HEADER_NAV_ICON_SLOT.w}
                    />
                  </View>
                ) : item.label === "Explore" ? (
                  <ExploreMenuDropdown
                    key={item.label}
                    iconSlotWidth={HEADER_NAV_ICON_SLOT.w}
                    iconSlotHeight={HEADER_NAV_ICON_SLOT.h}
                  />
                ) : (
                  <ExternalLink
                    key={item.label}
                    url={item.url}
                    onMouseEnter={() => setHoverHang(true)}
                    onMouseLeave={() => setHoverHang(false)}
                  >
                    <View
                      style={{
                        borderRadius: 6,
                        paddingVertical: 2,
                        paddingHorizontal: 2,
                      }}
                    >
                      <XStack
                        ai="center"
                        gap={6}
                        style={
                          isWeb
                            ? { color: hoverHang ? HEADER_HOVER_ORANGE : "white" }
                            : undefined
                        }
                      >
                        <View
                          style={{
                            width: HEADER_NAV_ICON_SLOT.w,
                            height: HEADER_NAV_ICON_SLOT.h,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <NavIconHang
                            width={36}
                            height={36}
                            color={hoverHang ? HEADER_HOVER_ORANGE : "white"}
                          />
                        </View>
                        <CardTitle
                          display="none"
                          $gtSm={{ display: "flex" }}
                          color={hoverHang ? HEADER_HOVER_ORANGE : "white"}
                          fontWeight="600"
                          style={{ cursor: "pointer" }}
                        >
                          {item.label}
                        </CardTitle>
                      </XStack>
                    </View>
                  </ExternalLink>
                ),
              )}
            </>
          )}

          {/* Live Sessions — mobile web only (native has it in the bottom bar) */}
          {isMobile && isWeb && (
            <View
              style={{
                flexShrink: 0,
              }}
            >
              <LiveSessionsDropdown
                membershipTier={membershipTier}
                membershipLoading={membershipLoading}
                showLabel={false}
                iconSize={HEADER_NAV_ICON_SLOT.w}
              />
            </View>
          )}

          {/* Bookmarks — desktop + mobile web (native has it in the bottom bar) */}
          {!(isMobile && !isWeb) && (
            <BookmarkManager>
              {isWeb ? (
                <div
                  onMouseEnter={() => setHoverBookmark(true)}
                  onMouseLeave={() => setHoverBookmark(false)}
                  style={{
                    color: hoverBookmark ? HEADER_HOVER_ORANGE : "white",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <XStack ai="center" gap={8}>
                    <NavIconBookmark
                      width={36}
                      height={36}
                      color={hoverBookmark ? HEADER_HOVER_ORANGE : "white"}
                    />
                  </XStack>
                </div>
              ) : (
                <XStack ai="center" gap={8} style={{ cursor: "pointer" }}>
                  <NavIconBookmark width={36} height={36} color="white" />
                </XStack>
              )}
            </BookmarkManager>
          )}

          {/* Global search - desktop/tablet only */}
          {!isMobile && <GlobalSearchManager />}

          {/* Announcements — signed-in; after search, same icon frame as bookmark (24) */}
          {user?.id ? (
            <View style={{ flexShrink: 0 }}>
              <AnnouncementsDropdown
                userId={user.id}
                iconSlotSize={HEADER_ACTION_ICON}
              />
            </View>
          ) : null}

          {/* Profile — desktop/tablet only; same optical weight as
              bookmark/bell (circle SVG fills more of its box, so slightly
              smaller than HEADER_ACTION_ICON). */}
          {!isMobile && <AccountMenu iconSize={24} />}

          {/* Hamburger menu — all mobile (web + native): opens the drawer */}
          {isMobile && (
            <Pressable onPress={openDrawer} hitSlop={8}>
              <View
                style={{
                  borderRadius: 6,
                  paddingVertical: 2,
                  paddingHorizontal: 2,
                }}
              >
                <View
                  style={{
                    width: HEADER_NAV_ICON_SLOT.w,
                    height: HEADER_NAV_ICON_SLOT.h,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MenuIcon width={28} height={28} />
                </View>
              </View>
            </Pressable>
          )}

          {/* Logout icon - COMMENTED OUT */}
          {/* <Pressable onPress={handleLogout} style={{ cursor: 'pointer' }}>
            <XStack ai="center" gap="$2">
              <Ionicons name="log-out-outline" size={16} color="white" />
              <Text display="none" $gtSm={{ display: 'flex' }} col="white" fow="600" fos={12}>
                Logout
              </Text>
            </XStack>
          </Pressable> */}
        </XStack>
      </XStack>
    </YStack>
  );

  // Return header + under-header alerts (below notch / safe area).
  if (Platform.OS === "web") {
    return (
      <YStack w="100%">
        {headerContent}
        <SubscriptionHeaderAlerts />
      </YStack>
    );
  }

  return (
    <YStack w="100%" backgroundColor={theme.header.val}>
      <StatusBar barStyle="light-content" backgroundColor={theme.header.val} />
      <YStack paddingTop={insets.top}>{headerContent}</YStack>
      <SubscriptionHeaderAlerts />
    </YStack>
  );
}
